import React, { useCallback, useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import ResponsiveTable from 'components/shared/tables/responsive/ResponsiveTable';
import AppBarButtonLink from 'components/shared/buttons/AppBarButtonLink';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import TrainerFilters from 'components/trainer/TrainerFilters';
import routes from 'common/routes';
import { getTrainerColumns } from 'common/table/columns';
import { getTrainerData } from 'common/table/data';
import { defaultTableOptions, getSelectToolBar, tableLocalization } from 'common/table/utils';
import { canModify } from 'common/utils/user';
import AppBarContext from 'context/AppBarContext';
import trainerSlice from 'store/modules/trainer';

function TrainerList() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'labels', 'routes', 'tables']);
  const pageTitle = t('trainer.list', { ns: 'routes' });
  const emptyMessage = t('empty', { type: pageTitle.toLowerCase(), ns: 'tables' });
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const filters = useSelector((state) => state.trainer.filters);
  const pagination = useSelector((state) => state.trainer.pagination);
  const paginatorInfo = useSelector((state) => state.trainer.paginatorInfo);
  const refresh = useSelector((state) => state.trainer.refresh);
  const role = useSelector((state) => state.auth.user.role);
  const status = useSelector((state) => state.trainer.status.general);
  const trainers = useSelector((state) => state.trainer.data.trainers);

  const canModifyData = canModify(role);

  const listTrainers = useCallback(() => {
    dispatch(trainerSlice.actions.list({ pagination, filters }));
  }, [dispatch, filters, pagination]);

  useEffect(() => {
    listTrainers();
  }, [listTrainers, refresh]);

  useEffect(() => {
    setAppBarButton(
      canModifyData ? (
        <AppBarButtonLink label={t('add', { ns: 'buttons' })} path={routes.createTrainer.path} />
      ) : null
    );
    setAppBarTitle(pageTitle);
  }, [canModifyData, pageTitle, setAppBarButton, setAppBarTitle]);

  const tableData = getTrainerData(trainers, t);
  const columns = getTrainerColumns({ data: tableData, role, translateFunc: t });

  const onChangePage = (page) => {
    dispatch(trainerSlice.actions.updatePagination({ page: page + 1 }));
  };

  const onDelete = (ids) => {
    dispatch(trainerSlice.actions.remove(ids));
  };

  const onRestore = (ids) => {
    dispatch(trainerSlice.actions.restore(ids));
  };

  const options = {
    ...defaultTableOptions,
    count: paginatorInfo.total,
    customToolbarSelect: (selectedRows, displayData) =>
      getSelectToolBar({
        filters,
        rows: selectedRows,
        data: displayData,
        onDelete,
        onRestore
      }),
    onChangePage,
    page: pagination.page - 1,
    rowsPerPage: pagination.first,
    selectableRows: canModifyData ? 'multiple' : 'none',
    selectableRowsHeader: canModifyData,
    textLabels: tableLocalization(pageTitle.toLowerCase(), t)
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <TrainerFilters />
      <Box
        sx={{
          position: 'relative'
        }}>
        <LoadingBlock
          emptyBlockProps={{ message: emptyMessage }}
          isValid={trainers.length > 0}
          skeletonProps={{ rows: 5 }}
          status={status}
          type="table">
          <ResponsiveTable
            className="user user--trainer"
            columns={columns}
            data={tableData}
            options={options}
            cardConfig={{ titleColumn: 'user', summaryColumns: ['country', 'age'] }}
          />
        </LoadingBlock>
      </Box>
    </>
  );
}

export default TrainerList;
